import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Download, FileText, FileImage, File, Loader2, AlertCircle, X, ZoomIn, ZoomOut } from 'lucide-react';
import { formatDistanceToNow } from '@/lib/utils';
import { getToken } from '@/lib/api';

const API_BASE = '/client/api';

/** Build a file URL with auth token for browser-native elements (img, iframe) */
function fileUrlWithToken(fileId: string): string {
  const token = getToken();
  let url = `${API_BASE}/files/${fileId}/download`;
  if (token) url += `?token=${encodeURIComponent(token)}`;
  return url;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

interface FilePreviewProps {
  file: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function isPreviewableMimeType(mimeType: string): string | null {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  return null;
}

export function FilePreview({ file, open, onOpenChange }: FilePreviewProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [zoomed, setZoomed] = useState(false);

  if (!file) return null;

  const previewType = isPreviewableMimeType(file.mimeType);
  const fileUrl = fileUrlWithToken(file.id);
  const downloadUrl = `${API_BASE}/files/${file.id}/download?token=${encodeURIComponent(getToken() || '')}`;
  const ext = file.originalName?.split('.').pop()?.toUpperCase() || 'FILE';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border-color)]">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            {/* File icon */}
            <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-dyndev-green/10 flex items-center justify-center">
              {previewType === 'image' ? (
                <FileImage size={16} className="text-dyndev-green" />
              ) : previewType === 'pdf' ? (
                <FileText size={16} className="text-red-400" />
              ) : (
                <File size={16} className="text-[var(--text-secondary)]" />
              )}
            </div>

            {/* File name and meta */}
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-sm font-medium truncate">
                {file.originalName}
              </DialogTitle>
              <div className="flex items-center gap-2 text-[10px] text-[var(--text-secondary)]">
                <span>{formatFileSize(file.size)}</span>
                <span>·</span>
                <Badge variant="outline" className="text-[9px] px-1 py-0">
                  {ext}
                </Badge>
                <span>·</span>
                <span>{file.uploadedBy?.name || 'Unknown'}</span>
                <span>·</span>
                <span>{formatDistanceToNow(new Date(file.createdAt))}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0 ml-4">
            {/* Zoom toggle for images */}
            {previewType === 'image' && (
              <button
                onClick={() => setZoomed(!zoomed)}
                className="p-1.5 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                title={zoomed ? 'Fit to view' : 'Zoom in'}
              >
                {zoomed ? <ZoomOut size={16} /> : <ZoomIn size={16} />}
              </button>
            )}

            {/* Download button */}
            <a
              href={downloadUrl}
              download={file.originalName}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-secondary)] hover:text-dyndev-green"
              title="Download"
            >
              <Download size={16} />
            </a>

            {/* Close button */}
            <button
              onClick={() => onOpenChange(false)}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Preview content */}
        <div className="flex-1 overflow-auto bg-[#0a0a0a] flex items-center justify-center min-h-[300px] max-h-[70vh]">
          {previewType === 'image' ? (
            <div className="relative flex items-center justify-center w-full h-full p-4">
              {/* Loading state */}
              {!imageLoaded && !imageError && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin text-dyndev-green" />
                </div>
              )}

              {/* Error state */}
              {imageError && (
                <div className="flex flex-col items-center gap-2 text-center">
                  <AlertCircle size={32} className="text-red-400" />
                  <p className="text-sm text-[var(--text-secondary)]">Failed to load image</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setImageError(false);
                      setImageLoaded(false);
                    }}
                  >
                    Retry
                  </Button>
                </div>
              )}

              {/* The image */}
              <img
                src={fileUrl}
                alt={file.originalName}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
                className={`transition-all duration-200 ${
                  zoomed
                    ? 'max-w-none cursor-zoom-out'
                    : 'max-w-full max-h-[65vh] object-contain cursor-zoom-in'
                } ${!imageLoaded && 'hidden'}`}
                onClick={() => setZoomed(!zoomed)}
                style={zoomed ? { maxWidth: 'none', maxHeight: 'none' } : {}}
              />
            </div>
          ) : previewType === 'pdf' ? (
            <iframe
              src={fileUrl}
              className="w-full h-[70vh] border-0"
              title={file.originalName}
              sandbox="allow-scripts allow-same-origin"
            />
          ) : (
            /* Non-previewable file — show info + download */
            <div className="flex flex-col items-center gap-4 p-8 text-center">
              <div className="w-16 h-16 rounded-xl bg-[var(--bg-secondary)] flex items-center justify-center border border-[var(--border-color)]">
                <File size={32} className="text-[var(--text-secondary)]" />
              </div>
              <div>
                <p className="text-sm font-medium mb-1">{file.originalName}</p>
                <p className="text-xs text-[var(--text-secondary)] mb-1">
                  {formatFileSize(file.size)} · {file.mimeType}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">
                  Preview not available for this file type.
                </p>
              </div>
              <a
                href={downloadUrl}
                download={file.originalName}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-dyndev-green text-white text-sm font-medium hover:bg-dyndev-green/90 transition-colors"
              >
                <Download size={14} />
                Download File
              </a>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
